import React from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Button } from '@parsina/framework';
import { User, Sparkles, Palette, Building2, ShieldCheck, Coins, Languages, LayoutPanelLeft, FileText, Pill } from 'lucide-react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// The settings section navigator. Same idiom as MobilePatientNav: a horizontal,
// thumb-scrollable strip, icon over label, active in navy with a navy underline,
// one page-level scroll below it (mobile concept §5, §6).
//
// The section SET comes from Settings.shared.js so both surfaces agree on what
// sections exist and in what order; only the icons are mobile's own.

const SECTION_ICONS = {
  profile:    User,
  ai:         Sparkles,
  visit:      LayoutPanelLeft,
  templates:  FileText,
  prescription: Pill,
  credits:    Coins,
  language:   Languages,
  appearance: Palette,
  clinics:    Building2,
  security:   ShieldCheck,
};

const MobileSettingsNav = ({ sections, active, onChange }) => {
  const { t } = useTranslation();
  const activeRef = React.useRef(null);
  React.useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [active]);

  return (
    <div
      data-testid="settings-nav"
      role="tablist"
      aria-label={t('settings.sectionsLabel')}
      style={{
        background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0,
        overflowX: 'auto', display: 'flex', scrollbarWidth: 'none',
        padding: '0 0.25rem',
        position: 'sticky', top: 0, zIndex: 2,
      }}
    >
      {sections.map(({ id, labelKey }) => {
        const Icon = SECTION_ICONS[id] || User;
        const on = active === id;
        return (
          <Button
            key={id}
            ref={on ? activeRef : undefined}
            variant="ghost"
            role="tab"
            aria-selected={on}
            data-testid={`settings-nav-${id}`}
            onClick={() => onChange(id)}
            className="h-auto min-h-11 flex-col gap-0.5 rounded-none px-3.5 py-2 shrink-0 hover:bg-transparent"
            style={{
              color: on ? C.navy : C.textMuted,
              fontWeight: on ? 700 : 500,
              borderBottom: on ? `2.5px solid ${C.navy}` : '2.5px solid transparent',
            }}
          >
            <Icon size={16} strokeWidth={on ? 2.4 : 2} aria-hidden />
            <span style={{ fontSize: '0.6875rem' }}>{t(labelKey)}</span>
          </Button>
        );
      })}
    </div>
  );
};

export default MobileSettingsNav;
